import { z } from "zod";
import { PlatformTypeZ, AdapterTypeZ } from "./platform";

export const AccountRoleZ = z.enum(["long", "short"]);
export type AccountRole = z.infer<typeof AccountRoleZ>;

export const AccountPhaseZ = z.enum(["step1", "step2", "funded", "blown"]);
export type AccountPhase = z.infer<typeof AccountPhaseZ>;

export const AccountStatusZ = z.enum(["active", "paused", "closed"]);
export type AccountStatus = z.infer<typeof AccountStatusZ>;

export const AccountZ = z.object({
  id: z.string().min(1).max(32),
  firmId: z.string().min(1).max(32),
  platformType: PlatformTypeZ,
  adapter: AdapterTypeZ,
  role: AccountRoleZ,
  pairId: z.string().nullable(),
  phase: AccountPhaseZ,
  status: AccountStatusZ.default("active"),
  challengeFee: z.number().nullable(),
  login: z.string().nullable(),
  brokerServer: z.string().nullable(),
  createdAt: z.number().int(),
});
export type Account = z.infer<typeof AccountZ>;

/** Snapshot of an account's equity/drawdown at a point in time. */
export const AccountSnapshotZ = z.object({
  accountId: z.string(),
  equity: z.number(),
  balance: z.number(),
  pnlOpen: z.number(),
  drawdownUsed: z.number().nullable(),
  dailyUsed: z.number().nullable(),
  maxDdPct: z.number().nullable(),
  dailyDdPct: z.number().nullable(),
  snapshotAt: z.number().int(),
});
export type AccountSnapshot = z.infer<typeof AccountSnapshotZ>;

/**
 * Normalized account state as reported by an adapter driver (MT5 EA or
 * Playwright platform driver). This is what gets cached in Redis under
 * `account:{id}:state` and pushed through the position webhook.
 */
export const AccountStateZ = z.object({
  accountId: z.string(),
  equity: z.number(),
  balance: z.number(),
  marginLevel: z.number().nullable(),
  openPnl: z.number(),
  closedPnl: z.number().nullable().optional(),
  drawdownUsed: z.number().nullable().optional(),
  dailyUsed: z.number().nullable().optional(),
});
export type AccountState = z.infer<typeof AccountStateZ>;
